import React, { useState, useEffect } from 'react';
import { useProfile } from 'src/hooks/useProfile';
import { FloatingInput } from '@components/ui/input-floating';
import { FloatingPlacesInput } from '@components/ui/input-places';
import { UserBasicsData } from 'src/services/interfaces/profile.interface';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { Check } from 'lucide-react';
import { useAuthStore } from 'src/store/auth';
import { FormMessageSpan } from '@components/ui/form';
import { authService } from 'src/services/auth';

export const ProfilePage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const { address, isLoading, updateProfile, updateProfileAsync, updateAddress, updateAddressAsync, updatePassword, passwordUpdateLoading, passwordUpdateError } = useProfile();
  const { user, setUserFromGetMe } = useAuthStore();
  const [editData, setEditData] = useState<UserBasicsData | null>(null);
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [profileErrors, setProfileErrors] = useState({
    firstName: '',
    lastName: '',
    midName: '',
    email: '',
    phone: '',
  });

  // Password validation helpers
  const validatePassword = (password: string) => {
    const isLengthValid = password.length >= 8;
    const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^A-Za-z0-9\s]/.test(password);
    
    return {
      isLengthValid,
      hasUpperLower,
      hasNumber,
      hasSymbol,
      isValid: isLengthValid && hasUpperLower && hasNumber && hasSymbol
    };
  };

  const passwordValidation = validatePassword(passwordData.newPassword);

  // Helper function to format full address for display
  const formatFullAddress = (address1: string, city: string, state: string, zipcode?: string, country?: string) => {
    const parts = [];
    if (address1) parts.push(address1);
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (zipcode) parts.push(zipcode);
    if (country && country !== 'US') parts.push(country); // Only show country if not US
    
    return parts.join(', ');
  };

  // Profile validation helpers
  const validateProfile = (data: UserBasicsData) => {
    const errors = {
      firstName: '',
      lastName: '',
      midName: '',
      email: '',
      phone: '',
    };

    // First Name validation (only letters)
    if (!data.firstName || !data.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(data.firstName.trim())) {
      errors.firstName = 'First name can only contain letters';
    }

    // Last Name validation (only letters)
    if (!data.lastName || !data.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(data.lastName.trim())) {
      errors.lastName = 'Last name can only contain letters';
    }

    // Middle Name validation (optional, only letters if provided)
    if (data.midName && data.midName.trim() && !/^[a-zA-Z\s]+$/.test(data.midName.trim())) {
      errors.midName = 'Middle name can only contain letters';
    }

    // Email validation
    if (!data.email || !data.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const email = data.email.trim();
      const atCount = (email.match(/@/g) || []).length;
      const [localPart, domain] = email.split('@');
      
      // Basic structure validation
      if (atCount !== 1 || !localPart || !domain || !domain.includes('.') || domain.startsWith('.') || domain.endsWith('.')) {
        errors.email = 'Please enter a valid email address';
      } else {
        // Local part validation - only allowed characters: letters, digits, and .!#$%&'*+-/=?^_`{|}~
        const localPartRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+$/;
        if (!localPartRegex.test(localPart)) {
          errors.email = 'Email contains invalid characters';
        }
      }
    }

    // Phone validation (optional, only digits and + symbol if provided)
    if (data.phone && data.phone.trim() && !/^[+\d\s()-]+$/.test(data.phone.trim())) {
      errors.phone = 'Phone number can only contain digits, +, spaces, (), and -';
    }

    return errors;
  };

  // Password criteria component
  const PasswordCriteria = ({
    isValid,
    label,
  }: {
    isValid: boolean;
    label: string;
  }) => (
    <div
      className={`flex items-center justify-center gap-0.5 px-2.5 h-6 rounded-full text-[9px] font-medium w-fit !font-secondary ${
        isValid ? 'bg-primary text-white' : 'bg-gray-200'
      }`}
    >
      <Check
        className={`w-3.5 h-3.5 ${isValid ? 'text-white' : 'text-[#a6a5a0]'}`}
        strokeWidth={3}
      />
      {label}
    </div>
  );

  useEffect(() => {
    if (user && address) {
      // Format full address for display
      const fullAddress = formatFullAddress(
        address.address1,
        address.city,
        address.state,
        address.zipcode
      );

      setEditData({
        firstName: user.firstName,
        lastName: user.lastName,
        midName: user.midName || '',
        email: user.email,
        phone: user.phone || '',
        address1: fullAddress, // Full formatted address for display
        address1Raw: address.address1, // Original street address for saving
        address2: address.address2,
        city: address.city,
        state: address.state,
        zipcode: address.zipcode || '',
        country: address.country || 'US',
      });
    }
  }, [user, address]);

  useEffect(() => {
    if (selectedPlace) {
      const addressComponents = selectedPlace.address_components;
      if (addressComponents) {
        const getAddressComponent = (type: string, field: string = 'long_name') => {
          const component = addressComponents.find((c) => c.types.includes(type)) as any;
          return component ? component[field] : '';
        };

        const streetNumber = getAddressComponent('street_number');
        const route = getAddressComponent('route');
        const city = getAddressComponent('locality');
        const state = getAddressComponent('administrative_area_level_1');
        const zipcode = getAddressComponent('postal_code', 'short_name');
        const country = getAddressComponent('country', 'short_name');
        
        // Build address components
        const streetAddress = `${streetNumber} ${route}`.trim();
        
        // For display in input field: [address1] [city], [state], [zipcode], [country]
        const displayParts = [];
        if (streetAddress) displayParts.push(streetAddress);
        if (city) displayParts.push(city);
        if (state) displayParts.push(state);
        if (zipcode) displayParts.push(zipcode);
        if (country) displayParts.push(country);
        
        const displayAddress = displayParts.join(', ');

        const newData = {
          address1: displayAddress, // Full address for display
          address1Raw: streetAddress, // Only street address for saving
          city,
          state,
          zipcode,
          country,
        };

        setEditData((prev) => ({
          ...prev!,
          ...newData
        }));
        
        // Reset selectedPlace to avoid re-triggering
        setSelectedPlace(null);
      }
    }
  }, [selectedPlace]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (user && address) {
      // Format full address for display
      const fullAddress = formatFullAddress(
        address.address1,
        address.city,
        address.state,
        address.zipcode
      );

      setEditData({
        firstName: user.firstName,
        lastName: user.lastName,
        midName: user.midName || '',
        email: user.email,
        phone: user.phone || '',
        address1: fullAddress, // Full formatted address for display
        address1Raw: address.address1, // Original street address for saving
        address2: address.address2,
        city: address.city,
        state: address.state,
        zipcode: address.zipcode || '',
        country: address.country || 'US',
      });
    }
    // Clear validation errors
    setProfileErrors({
      firstName: '',
      lastName: '',
      midName: '',
      email: '',
      phone: '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (editData) {
      // Validate profile data
      const validationErrors = validateProfile(editData);
      const hasErrors = Object.values(validationErrors).some(error => error !== '');
      
      if (hasErrors) {
        setProfileErrors(validationErrors);
        return;
      }

      // Clear errors if validation passes
      setProfileErrors({
        firstName: '',
        lastName: '',
        midName: '',
        email: '',
        phone: '',
      });

      // Prepare profile data, excluding empty optional fields
      const profileData: any = {
        firstName: editData.firstName,
        lastName: editData.lastName,
        email: editData.email,
        birthDate: '',
      };

      // Only include midName if it's not empty
      if (editData.midName && editData.midName.trim()) {
        profileData.midName = editData.midName;
      }

      // Only include phone if it's not empty
      if (editData.phone && editData.phone.trim()) {
        profileData.phone = editData.phone;
      }

      try {
        // Update profile and address
        // Prepare address data, excluding empty optional fields
        const addressData: any = {
          address1: editData.address1Raw || editData.address1, // Use raw street address for saving
          address2: editData.address2,
          city: editData.city,
          state: editData.state,
        };

        // Only include zipcode if it's not empty
        if (editData.zipcode && editData.zipcode.trim()) {
          addressData.zipcode = editData.zipcode;
        }

        // Only include country if it's not empty
        if (editData.country && editData.country.trim()) {
          addressData.country = editData.country;
        }

        await Promise.all([
          updateProfileAsync(profileData),
          updateAddressAsync(addressData)
        ]);

        // Refresh user data from server to sync auth store
        const updatedUser = await authService.getMe();
        setUserFromGetMe({
          ...updatedUser,
          username: updatedUser.email,
        });

        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update profile:', error);
        // Handle error if needed
      }
    }
  };

  const handleInputChange = (field: keyof UserBasicsData, value: string) => {
    setEditData((prev) => ({ ...prev!, [field]: value }));
    // Clear error when user starts typing
    if (profileErrors[field as keyof typeof profileErrors]) {
      setProfileErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (passwordErrors[field as keyof typeof passwordErrors]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordSave = () => {
    const errors = {
      newPassword: '',
      confirmPassword: '',
    };

    // Validate new password
    if (!passwordData.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (!passwordValidation.isValid) {
      errors.newPassword = 'Password must meet all requirements';
    }

    // Validate confirm password
    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords don't match";
    }

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    
    if (hasErrors) {
      setPasswordErrors(errors);
      return;
    }

    // If validation passes, update password
    updatePassword(
      {
        password: passwordData.newPassword,
      },
      {
        onSuccess: () => {
          setIsEditingPassword(false);
          setPasswordData({
            newPassword: '',
            confirmPassword: '',
          });
          setPasswordErrors({
            newPassword: '',
            confirmPassword: '',
          });
        },
        onError: (error: any) => {
          // Handle API errors
          const errorMessage = error?.response?.data?.message || 'Failed to update password';
          setPasswordErrors(prev => ({ ...prev, newPassword: errorMessage }));
        }
      }
    );
  };

  if (isLoading || !user) {
    return <div><AnimatedDots /></div>;
  }

  return (
    <div className="flex flex-col gap-8">
      <section
        className={`flex flex-col mt-5 rounded-3xl bg-profile-section-bg ${isEditing ? 'pb-7' : 'pb-16'} max-l:mt-0`}
      >
        <div className="bg-profile-header-bg text-white justify-between items-center flex px-7 max-md:px-10 rounded-t-3xl py-2 pb-2 text-[2vw] tracking-wide max-md:text-[3vw] max-sm:text-[4vw] max-xs:text-[5vw] max-md:py-5">
          <p>Your Basics</p>
          <button
            onClick={isEditing ? handleCancel : handleEdit}
            className="uppercase flex items-center gap-5"
          >
            <p className="font-bold text-base tracking-wider max-md:hidden">
              {isEditing ? 'CLOSE' : 'Edit'}
            </p>
            {isEditing && (
              <p className="font-bold text-base tracking-wider md:hidden">
                CLOSE
              </p>
            )}
            {!isEditing && (
              <img
                src="/icons/download.png"
                className="w-5.5"
                alt="download-pen"
              />
            )}
          </button>
        </div>
        <div className="flex flex-col px-13.5 mt-8 max-md:px-7 max-xs:px-5">
          <div className="flex flex-col gap-6">
            {editData &&
              Object.keys(editData).map((key) => (
                key === 'address2' || key === 'state' || key === 'zipcode' || key === 'city' || key === 'address1Raw' || key === 'country' ? null : (
                  <div key={key} className="flex gap-2 items-center">
                    <div className="w-[15%] max-lg:w-[18%] max-md:w-[25%] max-sm:w-[30%] max-2xs:w-[35%] text-profile-secondary uppercase font-bold tracking-wide">
                      {key.replace(/([A-Z])/g, ' $1').toUpperCase().replace('1', '')}
                    </div>
                    {isEditing ? (
                      <div className="w-1/2">
                        {key === 'address1' ? (
                          <FloatingPlacesInput
                            id={key}
                            label={key.replace(/([A-Z])/g, ' $1').toUpperCase().replace('1', '')}
                            type="text"
                            value={editData[key as keyof UserBasicsData]}
                            onChange={(e) => handleInputChange(key as keyof UserBasicsData, e.target.value)}
                            onPlaceSelect={setSelectedPlace}
                          />
                        ) : (
                          <>
                            <FloatingInput
                              id={key}
                              label={key.replace(/([A-Z])/g, ' $1').toUpperCase().replace('1', '')}
                              type={
                                key === 'email'
                                  ? 'email'
                                  : key === 'phone'
                                    ? 'tel'
                                    : 'text'
                              }
                              value={editData[key as keyof UserBasicsData]}
                              onChange={(e) =>
                                handleInputChange(
                                  key as keyof UserBasicsData,
                                  e.target.value,
                                )
                              }
                              inputClass={profileErrors[key as keyof typeof profileErrors] ? 'border-red-500' : ''}
                            />
                            {profileErrors[key as keyof typeof profileErrors] && (
                              <FormMessageSpan
                                className="text-error font-semibold text-sm mt-1"
                                text={profileErrors[key as keyof typeof profileErrors]}
                              />
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="font-bold text-black max-xs:text-sm">
                        {editData[key as keyof UserBasicsData]}
                      </div>
                    )}
                  </div>
                )
              ))}
          </div>
          {/* Hidden fields to preserve data */}
          {editData && (
            <div style={{ display: 'none' }}>
              <input type="hidden" value={editData.city} />
              <input type="hidden" value={editData.state} />
              <input type="hidden" value={editData.zipcode || ''} />
              <input type="hidden" value={editData.country || ''} />
            </div>
          )}
        </div>
        {isEditing && (
          <div className="flex justify-end gap-3 mt-4 px-7 max-xs:px-5">
            <span
              onClick={handleCancel}
              className="text-profile-secondary cursor-pointer font-bold text-sm uppercase tracking-wider"
            >
              CANCEL
            </span>
            <button
              onClick={handleSave}
              className="text-primary cursor-pointer font-bold text-sm uppercase tracking-wider"
            >
              SAVE
            </button>
          </div>
        )}
      </section>
      <section className="flex flex-col mt-5 rounded-3xl bg-profile-section-bg pb-10 max-l:mt-0">
        <div className="bg-profile-header-bg text-white justify-between items-center flex px-7 max-md:px-10 rounded-t-3xl py-2 pb-2 text-[2vw] tracking-wide max-md:text-[3vw] max-sm:text-[4vw] max-xs:text-[5vw] max-md:py-5">
          <p>Password and Security</p>
          <button onClick={() => setIsEditingPassword(!isEditingPassword)} className="uppercase flex items-center gap-5">
            <p className="font-bold text-base tracking-wider max-md:hidden">
              {isEditingPassword ? 'CLOSE' : 'Edit'}
            </p>
            <img
              src="/icons/download.png"
              className="w-5.5"
              alt="download-pen"
            />
          </button>
        </div>
        {isEditingPassword ? (
          <div className="flex flex-col gap-4 px-8.5 mt-10">
            <div>
              <FloatingInput
                id="new-password"
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                inputClass={passwordErrors.newPassword ? 'border-red-500' : ''}
              />
              {passwordErrors.newPassword && (
                <FormMessageSpan
                  className="text-sm mt-1"
                  text={passwordErrors.newPassword}
                />
              )}
              {passwordData.newPassword && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <PasswordCriteria
                    isValid={passwordValidation.isLengthValid}
                    label="At least 8 characters"
                  />
                  <PasswordCriteria
                    isValid={passwordValidation.hasUpperLower}
                    label="Upper and lower case"
                  />
                  <PasswordCriteria
                    isValid={passwordValidation.hasNumber && passwordValidation.hasSymbol}
                    label="A number and special character"
                  />
                </div>
              )}
            </div>
            <div>
              <FloatingInput
                id="confirm-password"
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                inputClass={passwordErrors.confirmPassword ? 'border-red-500' : ''}
              />
              {passwordErrors.confirmPassword && (
                <FormMessageSpan
                  className="text-sm mt-1"
                  text={passwordErrors.confirmPassword}
                />
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setIsEditingPassword(false)} className="text-profile-secondary font-bold text-sm uppercase tracking-wider">
                CANCEL
              </button>
              <button 
                onClick={handlePasswordSave} 
                disabled={passwordUpdateLoading}
                className="text-primary font-bold text-sm uppercase tracking-wider disabled:opacity-50"
              >
                {passwordUpdateLoading ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        ) : (
        <div className="flex px-8.5 max-1m:flex-col max-1m:items-start max-1m:gap-8 max-lg:px-6 items-end mt-10 max-md:px-7 gap-18 max-xs:px-5 max-lg:gap-6 max-md:gap-0">
          <div className="text-xl max-lg:text-base text-profile-secondary flex flex-col gap-6 max-1m:gap-4 uppercase font-bold tracking-wide max-w-[80%] max-l:max-w-full">
            <button 
              onClick={() => setIsEditingPassword(true)}
              className="text-xl max-lg:text-base text-profile-secondary uppercase font-bold tracking-wide cursor-pointer"
            >
              Click edit to change password
            </button>
            {/* <p className="">two-step authentication</p> */}
          </div>
          {/* <div className="flex gap-1">
            <button className="text-primary text-xl max-lg:text-base font-bold !font-secondary">
              ENABLED
            </button>
            <span className="!font-secondary text-lg max-lg:text-base max-md:text-sm font-bold">
              with phone number {user?.phone}
            </span>
          </div> */}
        </div>
        )}
      </section>
    </div>
  );
};
